import { Component } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import XLSX from 'sheetjs-style';
import Layout from './DashboardLayout';
import {
  MemberSearch,
  TeamMember,
  InvitationsListModal,
  AddMemberModal,
} from '../components/team';
import Loading from '../components/Loader';
import SwitchView from '../components/SwitchView';
import { resetMembersList, fetchOnlineMembers } from '../actions/team';
import { STATUS_CHECK_DELAY } from '../constants';
import { teamService } from '../services';

class Team extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExporting: false,
      isListView: false,
      isSubmitting: false,
      showInviteModal: false,
      membersCount: 0,
      pageHeading: {
        preTitle: 'Team',
        title: `Active Team Members`,
      },
      membersList: [],
    };
  }

  componentDidMount() {
    const { location } = this.props;
    const { getOnlineMembers } = this.props;
    // Fetch online members list onMount
    getOnlineMembers();
    // Check for online members every 2 minutes
    this.statusTimer = setInterval(
      () => getOnlineMembers(),
      STATUS_CHECK_DELAY
    );
    if (
      location.state &&
      location.state.action &&
      location.state.action === 'showInviteModal'
    ) {
      this.setState((state) => ({
        ...state,
        showInviteModal: false,
      }));
    }
  }

  componentDidUpdate(prevProps) {
    const { teamData } = this.props;
    if (teamData.membersCount !== prevProps.teamData.membersCount) {
      this.handleMembersCount(teamData.membersCount);
    }
  }

  componentWillUnmount() {
    const { resetMembers } = this.props;
    resetMembers();
    // Clear interval on un mount
    clearInterval(this.statusTimer);
  }

  exportTeamMembers = () => {
    this.setState({ isExporting: true });
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
    };

    teamService.export_team_members_list(params).then((result) => {
      this.createExcel((result && result.data) || []);
    });
  };

  // Capitalize the first letter in word
  capitalizeWords = (str) =>
    str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  createExcel = async (members) => {
    const workbook = XLSX.utils.book_new();

    const headers = [
      'Employee ID',
      'Name',
      'Email',
      'Role',
      'Projects',
      'Status',
    ];

    const tableData = members.map((member) => [
      member.employeeId || '',
      member.firstName || member.lastName
        ? this.capitalizeWords(member.firstName || member.lastName || '')
        : '',
      member.email,
      this.capitalizeWords(member.role || member.typeName || ''),
      member.projects.toString(),
      this.capitalizeWords(member.status === null ? 'Active' : member.status),
    ]);

    const worksheetData = [headers, ...tableData];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths (fixed)
    worksheet['!cols'] = [
      {}, // Emp ID
      { wch: 17 }, // Username
      { wch: 28 }, // Email
      { wch: 14 }, // Account Type
      { wch: 55 }, // Projects (fixed width)
      {}, // Status
    ];

    // Apply wrapText to 5th column (index 4)
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    // eslint-disable-next-line no-plusplus
    for (let rowEntry = 1; rowEntry <= range.e.r; ++rowEntry) {
      const cellRef = XLSX.utils.encode_cell({ r: rowEntry, c: 4 });
      const cell = worksheet[cellRef];
      if (cell) {
        cell.s = {
          alignment: { wrapText: true },
        };
      }
    }

    // Style (row 0)
    headers.forEach((header, col) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      const cell = worksheet[cellAddress];
      if (cell) {
        cell.s = {
          font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
          fill: {
            patternType: 'solid',
            fgColor: { rgb: '729FCF' },
          },
          alignment: { horizontal: 'center' },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          },
        };
      }
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const fileName = `MemberList.xlsx`;
    XLSX.writeFile(workbook, fileName);
    this.setState({ isExporting: false });
  };

  handleMembersCount = (value) => {
    this.setState((state) => ({
      ...state,
      pageHeading: {
        preTitle: 'Team',
        title: `Active Team Members (${value})`,
      },
    }));
  };

  handleChangeView = () => {
    this.setState((state) => ({
      ...state,
      isListView: !state.isListView,
    }));
  };

  toggleShowInviteModal = (displayState) => {
    this.setState((state) => ({
      ...state,
      showInviteModal: displayState,
    }));
  };

  render() {
    const { isListView, pageHeading, showInviteModal, isExporting } =
      this.state;
    const { permissions } = this.props;

    return (
      <Layout pageHeading={pageHeading}>
        <Row className="mb-4">
          <Col md="6" lg="3">
            <MemberSearch />
          </Col>
          <Col>
            <SwitchView
              isListView={isListView}
              handleChangeView={this.handleChangeView}
            />
          </Col>
          <Col className="col-auto ml-auto">
            <InvitationsListModal />
            {permissions && permissions.includes('team_member_add') && (
              <AddMemberModal
                showInviteModal={showInviteModal}
                toggleShowInviteModal={this.toggleShowInviteModal}
              />
            )}
            {permissions && permissions.includes('team_member_view') && (
              <Button
                className="btn btn-primary lift mb-1 me-2"
                onClick={(e) => {
                  this.exportTeamMembers(e);
                }}
                title="view all"
                type="button"
                disabled={isExporting}
              >
                {isExporting && (
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                Export Members
              </Button>
            )}
          </Col>
        </Row>
        <Row>
          <TeamMember isListView={isListView} />
        </Row>
      </Layout>
    );
  }
}

const mapStateToProps = (state) => ({
  teamData: state.team,
  permissions: state.permissionsReducer,
});

const mapDispatchToProps = () => ({
  resetMembers: resetMembersList,
  getOnlineMembers: fetchOnlineMembers,
});

Team.propTypes = {
  teamData: PropTypes.object,
  location: PropTypes.object,
  resetMembers: PropTypes.func,
  getOnlineMembers: PropTypes.func,
  permissions: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps())(Team);
